<script setup lang="ts">
/**
 * The "Details" disclosure under a reply's plain answer: the technical proof (ids, tools, how a run went), folded
 * away so the answer reads on its own and the proof is one press away. ZhChatThread draws it for a reply with a
 * Details section (lib/chat-details.ts). It folds the way the portal's tool log already does inside the same
 * bubble: a native disclosure, its summary the muted system label, a hairline above it.
 *
 * `open` is the default the caller gives, Advanced mode's open in the portal. The reader can open and close it by
 * hand, and a later change of `open` applies again, over whatever the reader did.
 */
import { ref, watch } from 'vue'
import ZhMarkdown from './ZhMarkdown.vue'
import ZhSystemLabel from './ZhSystemLabel.vue'

const props = defineProps<{
    /** The details, as the member wrote them (Markdown). */
    text: string
    /** Whether the disclosure is open: closed when omitted. */
    open?: boolean
}>()

const disclosure = ref<HTMLDetailsElement | null>(null)
const shown = ref(props.open === true)

watch(
    () => props.open === true,
    open => {
        shown.value = open
    },
)

/** The summary was pressed: it opens or closes here, so the state drawn is always the state held. */
function toggle(): void {
    shown.value = !shown.value
}

/** The browser opened or closed it by itself (find in page opens a closed one to show a match): keep up with it. */
function follow(): void {
    if (disclosure.value) {
        shown.value = disclosure.value.hasAttribute('open')
    }
}
</script>

<template>
    <details ref="disclosure" class="zh-chat-details" :open="shown" @toggle="follow">
        <summary class="zh-chat-details__summary" @click.prevent="toggle">
            <ZhSystemLabel as="span" tone="muted">Details</ZhSystemLabel>
        </summary>
        <ZhMarkdown class="zh-chat-details__body" :text="text" />
    </details>
</template>

<style scoped>
/* Under the answer, set off by the hairline the tool log in the same bubble uses. */
.zh-chat-details {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--hair);
}

/* A reply that is only details: the toggle is the whole bubble, so it needs no rule above it. */
.zh-chat-details:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: 0;
}

/* The disclosure's marker in the label's own muted grey: an indicator beside the word, not text. */
.zh-chat-details__summary {
    cursor: pointer;
    color: var(--muted-grey);
    user-select: none;
}

/* The proof reads a step quieter than the answer above it, on the kit's ink scale. */
.zh-chat-details__body {
    margin-top: 0.5rem;
    color: var(--ink-2);
}
</style>
